import React, { useState, useEffect, useRef } from "react";
import {
  FaTimes,
  FaCloudUploadAlt,
  FaPlus,
  FaCheckCircle,
  FaExclamationCircle,
  FaTag,
} from "react-icons/fa";

const API_BASE_URL = (process.env.REACT_APP_API_URL || "https://devserver.spatialgrid.ai/api/v1").replace(/\/$/, "");

/**
 * AddAssetModal
 *
 * Props:
 *  - show       {boolean}   Whether the modal is visible
 *  - onClose    {function}  Called when the modal should close
 *  - onSuccess  {function}  Called after a successful submission (optional)
 */
export default function AddAssetModal({ show, onClose, onSuccess }) {
  // "form" → upload step | "tags" → tag-editing step
  const [step, setStep] = useState("form");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitStatus, setSubmitStatus] = useState(""); // "success" | "error"

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    isPublic: true,
    type: "",
    subtype: "",
    industry: "",
  });
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [fileMeta, setFileMeta] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const fileMetaRef = useRef(null); // always holds latest value regardless of render cycle

  // Tag step state
  const [uploadedId, setUploadedId] = useState(null);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [tagMessage, setTagMessage] = useState("");
  const [tagStatus, setTagStatus] = useState(""); // "success" | "error"
  const tagInputRef = useRef(null);

  // Reset form whenever the modal opens
  useEffect(() => {
    if (show) {
      setStep("form");
      setFormData({ name: "", description: "", isPublic: true, type: "", subtype: "", industry: "" });
      setFile(null);
      setThumbnail(null);
      setFileMeta(null);
      fileMetaRef.current = null;
      setIsExtracting(false);
      setSubmitMessage("");
      setSubmitStatus("");
      setUploadedId(null);
      setTags([]);
      setNewTag("");
      setTagMessage("");
      setTagStatus("");
    }
  }, [show]);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = show ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [show]);

  if (!show) return null;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const extractPolyCount = async (selectedFile, ext) => {
    try {
      if (ext === "glb") {
        const buffer = await selectedFile.arrayBuffer();
        const view = new DataView(buffer);
        if (view.getUint32(0, true) !== 0x46546c67) return null; // not glTF magic
        const chunk0Type = view.getUint32(16, true);
        if (chunk0Type !== 0x4e4f534a) return null; // not JSON chunk
        const chunk0Len = view.getUint32(12, true);
        const json = JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, 20, chunk0Len)));
        let tris = 0;
        for (const mesh of json.meshes ?? []) {
          for (const prim of mesh.primitives ?? []) {
            const accIdx = prim.indices ?? prim.attributes?.POSITION;
            if (accIdx != null) tris += json.accessors[accIdx].count / 3;
          }
        }
        return Math.round(tris);
      }

      if (ext === "gltf") {
        const text = await selectedFile.text();
        const json = JSON.parse(text);
        let tris = 0;
        for (const mesh of json.meshes ?? []) {
          for (const prim of mesh.primitives ?? []) {
            const accIdx = prim.indices ?? prim.attributes?.POSITION;
            if (accIdx != null) tris += json.accessors[accIdx].count / 3;
          }
        }
        return Math.round(tris);
      }

      if (ext === "obj") {
        const text = await selectedFile.text();
        let tris = 0;
        for (const line of text.split("\n")) {
          if (!line.startsWith("f ")) continue;
          const verts = line.trim().split(/\s+/).length - 1;
          tris += verts - 2; // fan triangulation: quad → 2 tris, tri → 1
        }
        return tris;
      }

      if (ext === "stl") {
        const buffer = await selectedFile.arrayBuffer();
        const header = new TextDecoder().decode(new Uint8Array(buffer, 0, 5));
        if (header === "solid") {
          const text = new TextDecoder().decode(new Uint8Array(buffer));
          return (text.match(/facet normal/g) ?? []).length;
        }
        return new DataView(buffer).getUint32(80, true);
      }
    } catch {
      // parsing failed — return null gracefully
    }
    return null;
  };

  const extractFileMeta = async (selectedFile) => {
    const ext = selectedFile.name.split(".").pop().toLowerCase();
    const base = {
      fileSize: selectedFile.size,
      format: ext,
      resolution: null,
      polyCount: null,
    };
    const mime = selectedFile.type;

    if (mime.startsWith("image/")) {
      return new Promise((resolve) => {
        const url = URL.createObjectURL(selectedFile);
        const img = new Image();
        img.onload = () => { resolve({ ...base, resolution: `${img.naturalWidth}x${img.naturalHeight}` }); URL.revokeObjectURL(url); };
        img.onerror = () => { resolve(base); URL.revokeObjectURL(url); };
        img.src = url;
      });
    }

    if (mime.startsWith("video/")) {
      return new Promise((resolve) => {
        const url = URL.createObjectURL(selectedFile);
        const vid = document.createElement("video");
        vid.onloadedmetadata = () => { resolve({ ...base, resolution: `${vid.videoWidth}x${vid.videoHeight}` }); URL.revokeObjectURL(url); };
        vid.onerror = () => { resolve(base); URL.revokeObjectURL(url); };
        vid.src = url;
      });
    }

    if (["glb", "gltf", "obj", "stl"].includes(ext)) {
      const polyCount = await extractPolyCount(selectedFile, ext);
      return { ...base, polyCount };
    }

    return base;
  };

  const handleFileChange = async (e) => {
    const { name, files } = e.target;
    if (name === "file") {
      const selected = files[0];
      setFile(selected);
      setFileMeta(null);
      fileMetaRef.current = null;
      if (selected) {
        setIsExtracting(true);
        try {
          const meta = await extractFileMeta(selected);
          fileMetaRef.current = meta;
          setFileMeta(meta);
        } catch {
          // extraction failed — send without metadata rather than block upload
          fileMetaRef.current = null;
          setFileMeta(null);
        } finally {
          setIsExtracting(false);
        }
      }
    } else if (name === "thumbnail") {
      setThumbnail(files[0]);
    }
  };

  /* ── Step 1: Upload ───────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage("");
    setSubmitStatus("");

    const token = localStorage.getItem("accessToken");
    if (!token) {
      setSubmitStatus("error");
      setSubmitMessage("No access token found. Please log in again.");
      setIsSubmitting(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === 'subtype') {
          // When an industry is selected, use it as the grouping key (subtype)
          // so the library groups items by industry, not by generic category.
          formDataToSend.append('subtype', formData.industry || formData.subtype);
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });
      if (file) formDataToSend.append("file", file);
      if (thumbnail) formDataToSend.append("thumbnail", thumbnail);
      // Read from ref — guaranteed latest value even if React state hasn't re-rendered yet
      const currentMeta = fileMetaRef.current;
      if (currentMeta) {
        formDataToSend.append("metadata", JSON.stringify(currentMeta));
        // Also send flat fields — some backends read individual fields instead of parsing the JSON string
        if (currentMeta.fileSize != null) formDataToSend.append("fileSize", String(currentMeta.fileSize));
        if (currentMeta.format)           formDataToSend.append("format", currentMeta.format);
        if (currentMeta.resolution)       formDataToSend.append("resolution", currentMeta.resolution);
        if (currentMeta.polyCount != null) formDataToSend.append("polyCount", String(currentMeta.polyCount));
      }

      const response = await fetch(`${API_BASE_URL}/library`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formDataToSend,
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("userData");
          throw new Error("Unauthorized. Please log in again.");
        }
        throw new Error(result.message || "Failed to submit library item");
      }

      // Transition to tag-editing step
      setUploadedId(result.data?._id);
      setTags(Array.isArray(result.suggestedTags) ? result.suggestedTags : []);
      onSuccess?.();
      setStep("tags");
    } catch (error) {
      setSubmitStatus("error");
      setSubmitMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── Step 2: Tag editing ──────────────────────────────────────── */
  const handleRemoveTag = (tagToRemove) => {
    setTags((prev) => prev.filter((t) => t !== tagToRemove));
  };

  const handleAddTag = () => {
    const trimmed = newTag.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      setTags((prev) => [...prev, trimmed]);
    }
    setNewTag("");
    tagInputRef.current?.focus();
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSaveTags = async () => {
    if (!uploadedId) return;
    setIsSavingTags(true);
    setTagMessage("");
    setTagStatus("");

    const token = localStorage.getItem("accessToken");
    try {
      const response = await fetch(
        `${API_BASE_URL}/library/${uploadedId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ tags: JSON.stringify(tags) }),
        },
      );

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || "Failed to save tags");
      }

      setTagStatus("success");
      setTagMessage("Tags saved!");
      setTimeout(() => onClose(), 1200);
    } catch (error) {
      setTagStatus("error");
      setTagMessage(error.message);
    } finally {
      setIsSavingTags(false);
    }
  };

  // Shared Tailwind classes
  const inputCls =
    "w-full px-4 py-2.5 rounded-xl bg-[#2d2e32] text-gray-200 border border-[#2e2f33] text-sm outline-none transition-all duration-200 focus:border-[#EE2B2A] focus:ring-2 focus:ring-[#EE2B2A]/10";

  const labelCls =
    "block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5";

  return (
    <>
      <style>{`
        @keyframes am-fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes am-slideUp { from { opacity: 0; transform: translateY(28px) scale(0.97) } to { opacity: 1; transform: translateY(0) scale(1) } }
        .am-fadeIn  { animation: am-fadeIn  0.2s ease-out forwards; }
        .am-slideUp { animation: am-slideUp 0.28s cubic-bezier(0.22,1,0.36,1) forwards; }
        .am-scroll::-webkit-scrollbar { display: none; }
        .am-scroll  { scrollbar-width: none; }
      `}</style>

      {/* ── Backdrop ── */}
      <div
        className="am-fadeIn fixed inset-0 z-50 flex items-center justify-center p-5 bg-black/75 backdrop-blur-md"
      >
        {/* ── Panel ── */}
        <div
          className="am-slideUp am-scroll relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#1a1b1f] border border-[#2a2b2f] rounded-2xl shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Header ── */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-7 py-5 bg-[#1e1f22] border-b border-[#22232a] rounded-t-2xl">
            <div>
              <h2 className="text-xl font-bold text-gray-100 leading-tight">
                {step === "form" ? "Add New Asset" : "Review Tags"}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {step === "form"
                  ? "Upload and configure your digital asset"
                  : "AI-suggested tags — remove or add before saving"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-[#26272b] border border-[#2e2f33] text-gray-500 text-sm transition-all duration-200 hover:bg-[#EE2B2A] hover:text-white hover:border-[#EE2B2A]"
            >
              <FaTimes />
            </button>
          </div>

          {/* ══════════════════ STEP 1 — Upload form ══════════════════ */}
          {step === "form" && (
            <form onSubmit={handleSubmit} className="px-7 py-6 flex flex-col gap-5">
              {/* Type & Subtype */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>
                    Asset Type <span className="text-[#EE2B2A]">*</span>
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className={inputCls}
                    required
                  >
                    <option value="">Select type…</option>
                    <option value="3D">3D Assets</option>
                    <option value="template">Templates</option>
                    <option value="scene">Scenes</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="audio">Audio</option>
                    <option value="text">Text</option>
                    <option value="vector">Vectors</option>
                    <option value="sprite">Sprites</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Category</label>
                  <select
                    name="subtype"
                    value={formData.subtype}
                    onChange={handleInputChange}
                    className={inputCls}
                  >
                    <option value="">Select category…</option>
                    <option value="character">Character</option>
                    <option value="object">Object</option>
                  </select>
                </div>
              </div>

              {/* Industry */}
              <div>
                <label className={labelCls}>Industry</label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                  className={inputCls}
                >
                  <option value="">Select industry…</option>
                  <option value="Manufacturing & Industrial">Manufacturing &amp; Industrial</option>
                  <option value="Healthcare and Pharma">Healthcare and Pharma</option>
                  <option value="Retail & E-Commerce">Retail &amp; E-Commerce</option>
                  <option value="Education & Training">Education &amp; Training</option>
                  <option value="Automotive & Transportation">Automotive &amp; Transportation</option>
                  <option value="Architecture & Real Estate">Architecture &amp; Real Estate</option>
                  <option value="Defense & Aerospace">Defense &amp; Aerospace</option>
                  <option value="Energy and Mining">Energy and Mining</option>
                  <option value="Tourism & Hospitality">Tourism &amp; Hospitality</option>
                </select>
              </div>

              {/* Name */}
              <div>
                <label className={labelCls}>
                  Name <span className="text-[#EE2B2A]">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Asset name"
                  className={inputCls}
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className={labelCls}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your asset…"
                  className={`${inputCls} resize-y`}
                  rows={3}
                />
              </div>

              {/* File Uploads */}
              <div className="grid grid-cols-2 gap-4">
                {/* Thumbnail */}
                <div>
                  <label className={labelCls}>Thumbnail</label>
                  <input
                    type="file"
                    name="thumbnail"
                    id="am-thumbnail"
                    onChange={handleFileChange}
                    accept="image/*"
                    className="hidden"
                  />
                  <label
                    htmlFor="am-thumbnail"
                    className={`flex flex-col items-center justify-center w-full h-28 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200
                      ${thumbnail ? "border-[#EE2B2A] bg-[#EE2B2A]/5" : "border-[#2e2f33] bg-[#2d2e32]"}`}
                  >
                    <FaCloudUploadAlt
                      className={`text-2xl mb-1.5 transition-colors duration-200 ${thumbnail ? "text-[#EE2B2A]" : "text-gray-600"}`}
                    />
                    <span className={`text-xs text-center px-2 truncate max-w-full ${thumbnail ? "text-gray-200" : "text-gray-600"}`}>
                      {thumbnail ? thumbnail.name : "Click to upload image"}
                    </span>
                  </label>
                </div>

                {/* Asset File */}
                <div>
                  <label className={labelCls}>Asset File</label>
                  <input
                    type="file"
                    name="file"
                    id="am-file"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="am-file"
                    className={`flex flex-col items-center justify-center w-full h-28 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200
                      ${file ? "border-[#EE2B2A] bg-[#EE2B2A]/5" : "border-[#2e2f33] bg-[#2d2e32]"}`}
                  >
                    <FaCloudUploadAlt
                      className={`text-2xl mb-1.5 transition-colors duration-200 ${file ? "text-[#EE2B2A]" : "text-gray-600"}`}
                    />
                    <span className={`text-xs text-center px-2 truncate max-w-full ${file ? "text-gray-200" : "text-gray-600"}`}>
                      {file ? file.name : "Click to upload asset"}
                    </span>
                  </label>
                </div>
              </div>

              {/* Extracted metadata preview */}
              {fileMeta && (
                <div className="px-4 py-3 bg-[#2d2e32] border border-[#2e2f33] rounded-xl">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-2">
                    Detected Metadata
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: "fileSize", value: `${(fileMeta.fileSize / 1048576).toFixed(2)} MB` },
                      { label: "format", value: fileMeta.format },
                      fileMeta.resolution && { label: "resolution", value: fileMeta.resolution },
                      fileMeta.polyCount != null && { label: "polyCount", value: fileMeta.polyCount.toLocaleString() },
                    ]
                      .filter(Boolean)
                      .map(({ label, value }) => (
                        <span key={label} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs bg-[#1a1b1f] border border-[#3a3b3f] text-gray-400">
                          <span className="text-gray-600">{label}:</span> {value}
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {/* Public Toggle */}
              <label className="flex items-center gap-3 px-4 py-3 bg-[#2d2e32] border border-[#2e2f33] rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  name="isPublic"
                  checked={formData.isPublic}
                  onChange={handleInputChange}
                  className="w-4 h-4 accent-[#EE2B2A] cursor-pointer"
                />
                <span className="text-sm text-gray-400 select-none">
                  Make this asset publicly accessible
                </span>
              </label>

              {/* Status Message */}
              {submitMessage && (
                <div
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm border ${
                    submitStatus === "success"
                      ? "bg-green-500/10 border-green-500/25 text-green-400"
                      : "bg-red-500/10 border-red-500/25 text-red-400"
                  }`}
                >
                  {submitStatus === "success" ? (
                    <FaCheckCircle className="shrink-0" />
                  ) : (
                    <FaExclamationCircle className="shrink-0" />
                  )}
                  {submitMessage}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 mt-1 border-t border-[#22232a]">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-[#26272b] hover:bg-[#303136] text-gray-400 hover:text-gray-200 border border-[#2e2f33] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaTimes className="text-xs" />
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting || isExtracting}
                  className="flex items-center gap-2 px-7 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#ee2b2A] disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0"
                >
                  {isExtracting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                      Extracting…
                    </>
                  ) : isSubmitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <FaPlus />
                      Upload Asset
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* ══════════════════ STEP 2 — Tag editing ══════════════════ */}
          {step === "tags" && (
            <div className="px-7 py-6 flex flex-col gap-6">
              {/* Info banner */}
              <div className="flex items-start gap-3 px-4 py-3 bg-[#EE2B2A]/8 border border-[#EE2B2A]/20 rounded-xl">
                <FaTag className="text-[#EE2B2A] shrink-0 mt-0.5" />
                <p className="text-sm text-gray-300">
                  These tags were suggested by AI based on your asset. Remove ones you don't want and add any custom tags before saving.
                </p>
              </div>

              {/* Tag chips */}
              <div>
                <label className={labelCls}>Tags</label>
                <div className="flex flex-wrap gap-2 min-h-[48px] px-4 py-3 bg-[#2d2e32] border border-[#2e2f33] rounded-xl">
                  {tags.length === 0 && (
                    <span className="text-sm text-gray-600 self-center">No tags yet — add some below</span>
                  )}
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-[#EE2B2A]/15 text-[#EE2B2A] border border-[#EE2B2A]/30"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-white transition-colors duration-150 leading-none"
                        aria-label={`Remove tag ${tag}`}
                      >
                        <FaTimes className="text-[10px]" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Add custom tag */}
              <div>
                <label className={labelCls}>Add a custom tag</label>
                <div className="flex gap-2">
                  <input
                    ref={tagInputRef}
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={handleTagInputKeyDown}
                    placeholder="Type a tag and press Enter…"
                    className={inputCls}
                    maxLength={40}
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    disabled={!newTag.trim()}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-[#26272b] hover:bg-[#303136] text-gray-300 border border-[#2e2f33] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                  >
                    <FaPlus className="text-xs" />
                    Add
                  </button>
                </div>
              </div>

              {/* Tag save status */}
              {tagMessage && (
                <div
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm border ${
                    tagStatus === "success"
                      ? "bg-green-500/10 border-green-500/25 text-green-400"
                      : "bg-red-500/10 border-red-500/25 text-red-400"
                  }`}
                >
                  {tagStatus === "success" ? (
                    <FaCheckCircle className="shrink-0" />
                  ) : (
                    <FaExclamationCircle className="shrink-0" />
                  )}
                  {tagMessage}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 mt-1 border-t border-[#22232a]">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSavingTags}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-[#26272b] hover:bg-[#303136] text-gray-400 hover:text-gray-200 border border-[#2e2f33] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Skip
                </button>

                <button
                  type="button"
                  onClick={handleSaveTags}
                  disabled={isSavingTags}
                  className="flex items-center gap-2 px-7 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#ee2b2A] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingTags ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <FaCheckCircle />
                      Save Tags
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
